import React, { useEffect, useState, useCallback } from "react";
import api from "../../apiClient";

export default function ValueTrackerApp({ onClose }) {
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [interval, setInterval_] = useState(60);
  const [accounts, setAccounts] = useState({});
  const [toast, setToast] = useState("");

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  // Load config + status on mount
  useEffect(() => {
    Promise.all([
      api.get("/cad/value-tracker/config"),
      api.get("/cad/value-tracker"),
    ])
      .then(([cfgRes, statusRes]) => {
        const cfg = cfgRes.data;
        setConfig(cfg);
        setInterval_(cfg.check_interval_minutes || 60);
        setAccounts(cfg.accounts || {});
        setStatus(statusRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const res = await api.put("/cad/value-tracker/config", {
        check_interval_minutes: interval,
        accounts,
      });
      setConfig((prev) => ({ ...prev, ...res.data }));
      showToast("Config saved. Cache cleared — next check will use new settings.");
    } catch (err) {
      showToast(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [interval, accounts]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await api.post("/cad/value-tracker/refresh");
      setStatus(res.data);
      showToast("Check complete.");
    } catch (err) {
      showToast(err?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  }, []);

  const updateAccounts = (cadKey, value) => {
    const list = value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    setAccounts((prev) => ({ ...prev, [cadKey]: list }));
  };

  if (loading) {
    return (
      <div className="vt-app">
        <style>{styles}</style>
        <div className="vt-loading">Loading...</div>
      </div>
    );
  }

  const supported = config?.supported_cads || [];
  const displayNames = config?.display_names || {};
  const statusByKey = {};
  (status?.cads || []).forEach((c) => (statusByKey[c.key] = c));

  return (
    <div className="vt-app">
      <style>{styles}</style>

      {/* Header */}
      <header className="vt-header">
        <button className="vt-back" onClick={onClose}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          Home
        </button>
        <h1 className="vt-title">Value Release Tracker</h1>
        <div className="vt-header-actions">
          <button className="vt-btn vt-btn-outline" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? "Checking..." : "Run Check Now"}
          </button>
          <button className="vt-btn vt-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Config"}
          </button>
        </div>
      </header>

      {/* Toast */}
      {toast && <div className="vt-toast">{toast}</div>}

      {/* Status banner */}
      {status && (
        <div className="vt-status-banner">
          <div className="vt-status-header">
            <div className="vt-live-dot" />
            <span className="vt-status-label">
              {status.year} Release Status
            </span>
            <span className="vt-status-time">
              Last checked: {status.checked_at ? new Date(status.checked_at).toLocaleString() : "—"}
            </span>
          </div>
          <div className="vt-status-chips">
            {(status.cads || []).map((c) => (
              <div key={c.key} className={`vt-chip ${c.released ? "vt-chip-on" : ""}`}>
                <div className={`vt-chip-dot ${c.released ? "vt-chip-dot-on" : ""}`} />
                <span className="vt-chip-name">{c.name}</span>
                <span className="vt-chip-count">{c.hits}/{c.sample_size}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Interval setting */}
      <div className="vt-section">
        <h2 className="vt-section-title">Check Interval</h2>
        <p className="vt-section-desc">
          How often to re-check CAD sites for new values. Cached results are served between checks.
        </p>
        <div className="vt-interval-row">
          <input
            type="number"
            min={1}
            max={1440}
            value={interval}
            onChange={(e) => setInterval_(parseInt(e.target.value, 10) || 60)}
            className="vt-input vt-input-sm"
          />
          <span className="vt-interval-label">minutes</span>
          <span className="vt-interval-hint">
            ({interval >= 60 ? `${(interval / 60).toFixed(1)}h` : `${interval}m`})
          </span>
        </div>
      </div>

      {/* Sample accounts per CAD */}
      <div className="vt-section">
        <h2 className="vt-section-title">Sample Accounts</h2>
        <p className="vt-section-desc">
          Comma-separated account numbers to check per CAD. These are the accounts used to detect if values have been released.
        </p>
        <div className="vt-accounts-grid">
          {supported.map((key) => {
            const s = statusByKey[key];
            const released = s?.released;
            return (
              <div key={key} className={`vt-account-card ${released ? "vt-account-card-on" : ""}`}>
                <div className="vt-account-header">
                  <div className={`vt-account-dot ${released ? "vt-account-dot-on" : ""}`} />
                  <span className="vt-account-name">{displayNames[key] || key}</span>
                  {s && (
                    <span className={`vt-account-badge ${released ? "vt-account-badge-on" : ""}`}>
                      {released ? `${s.hits}/${s.sample_size} hit` : "pending"}
                    </span>
                  )}
                </div>
                <textarea
                  className="vt-account-input"
                  rows={2}
                  value={(accounts[key] || []).join(", ")}
                  onChange={(e) => updateAccounts(key, e.target.value)}
                  placeholder="Enter account numbers..."
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const styles = `
.vt-app{
  min-height:100vh;background:#F5F6FA;
  font-family:'Montserrat',-apple-system,BlinkMacSystemFont,sans-serif;
  color:#222D50;padding-bottom:40px;
}
.vt-loading{padding:80px;text-align:center;color:#8E96AD;font-size:14px;}
.vt-header{
  display:flex;align-items:center;gap:16px;flex-wrap:wrap;
  padding:16px 32px;background:white;
  border-bottom:1px solid #EEEFF4;
  position:sticky;top:0;z-index:10;
}
.vt-back{
  display:flex;align-items:center;gap:4px;
  background:none;border:1px solid #EEEFF4;border-radius:8px;
  padding:6px 12px;cursor:pointer;color:#8E96AD;
  font-family:inherit;font-size:12px;font-weight:600;
  transition:all .2s;
}
.vt-back:hover{border-color:#D8DBE6;color:#222D50;}
.vt-title{font-size:18px;font-weight:800;color:#222D50;margin-right:auto;}
.vt-header-actions{display:flex;gap:8px;}
.vt-btn{
  padding:8px 16px;border-radius:8px;
  font-family:inherit;font-size:12px;font-weight:700;
  cursor:pointer;transition:all .2s;border:none;
}
.vt-btn:disabled{opacity:.5;cursor:not-allowed;}
.vt-btn-outline{
  background:white;border:1px solid #EEEFF4;color:#222D50;
}
.vt-btn-outline:hover:not(:disabled){border-color:#D8DBE6;box-shadow:0 2px 8px rgba(0,0,0,.04);}
.vt-btn-primary{
  background:#0E1B3D;color:white;
}
.vt-btn-primary:hover:not(:disabled){background:#1A2E5A;}

.vt-toast{
  position:fixed;top:16px;right:16px;z-index:100;
  background:#0E1B3D;color:white;
  padding:10px 20px;border-radius:10px;
  font-size:12px;font-weight:600;
  box-shadow:0 8px 24px rgba(0,0,0,.15);
  animation:vt-toast-in .3s ease;
}
@keyframes vt-toast-in{0%{opacity:0;transform:translateY(-8px);}100%{opacity:1;transform:translateY(0);}}

.vt-status-banner{
  margin:20px 32px 0;
  background:linear-gradient(135deg,#0E1B3D 0%,#1A2E5A 100%);
  border-radius:14px;padding:18px 22px;color:white;
}
.vt-status-header{
  display:flex;align-items:center;gap:10px;margin-bottom:14px;flex-wrap:wrap;
}
.vt-live-dot{
  width:8px;height:8px;border-radius:50%;background:#4ADE80;
  box-shadow:0 0 8px rgba(74,222,128,.6);
  animation:vt-pulse 2s ease-in-out infinite;
}
@keyframes vt-pulse{0%,100%{opacity:1;}50%{opacity:.4;}}
.vt-status-label{font-size:13px;font-weight:700;letter-spacing:.5px;}
.vt-status-time{margin-left:auto;font-size:10px;color:rgba(255,255,255,.4);font-weight:500;}
.vt-status-chips{display:flex;flex-wrap:wrap;gap:6px;}
.vt-chip{
  display:flex;align-items:center;gap:6px;
  background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.08);
  border-radius:8px;padding:5px 10px;
  font-size:11px;font-weight:500;color:rgba(255,255,255,.45);
}
.vt-chip-on{background:rgba(74,222,128,.08);border-color:rgba(74,222,128,.2);color:rgba(255,255,255,.9);}
.vt-chip-dot{width:6px;height:6px;border-radius:50%;background:rgba(255,255,255,.2);}
.vt-chip-dot-on{background:#4ADE80;box-shadow:0 0 6px rgba(74,222,128,.5);}
.vt-chip-name{font-weight:600;}
.vt-chip-count{font-size:10px;color:rgba(255,255,255,.3);}
.vt-chip-on .vt-chip-count{color:rgba(74,222,128,.7);}

.vt-section{
  margin:20px 32px 0;background:white;
  border-radius:14px;border:1px solid #EEEFF4;
  padding:20px 24px;
}
.vt-section-title{font-size:14px;font-weight:800;margin-bottom:4px;}
.vt-section-desc{font-size:11px;color:#8E96AD;margin-bottom:14px;line-height:1.5;}
.vt-interval-row{display:flex;align-items:center;gap:10px;}
.vt-input{
  border:1px solid #EEEFF4;border-radius:8px;
  padding:8px 12px;font-family:inherit;font-size:13px;
  font-weight:600;color:#222D50;outline:none;
  transition:border-color .2s;
}
.vt-input:focus{border-color:#6E84C7;}
.vt-input-sm{width:100px;}
.vt-interval-label{font-size:13px;font-weight:600;color:#8E96AD;}
.vt-interval-hint{font-size:11px;color:#B8BFD0;}

.vt-accounts-grid{
  display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));
  gap:10px;
}
.vt-account-card{
  border:1px solid #EEEFF4;border-radius:10px;
  padding:12px 14px;transition:all .2s;
}
.vt-account-card-on{border-color:rgba(74,222,128,.3);background:#F8FFF9;}
.vt-account-header{
  display:flex;align-items:center;gap:8px;margin-bottom:8px;
}
.vt-account-dot{width:8px;height:8px;border-radius:50%;background:#E0E3EA;flex-shrink:0;}
.vt-account-dot-on{background:#4ADE80;box-shadow:0 0 6px rgba(74,222,128,.4);}
.vt-account-name{font-size:12px;font-weight:700;flex:1;}
.vt-account-badge{
  font-size:9px;font-weight:700;
  padding:2px 8px;border-radius:6px;
  background:#F0F1F5;color:#8E96AD;text-transform:uppercase;letter-spacing:.5px;
}
.vt-account-badge-on{background:rgba(74,222,128,.15);color:#16A34A;}
.vt-account-input{
  width:100%;border:1px solid #EEEFF4;border-radius:8px;
  padding:8px 10px;font-family:inherit;font-size:11px;
  color:#222D50;resize:vertical;outline:none;
  transition:border-color .2s;
}
.vt-account-input:focus{border-color:#6E84C7;}

@media(max-width:768px){
  .vt-header{padding:12px 16px;}
  .vt-status-banner,.vt-section{margin-left:16px;margin-right:16px;}
  .vt-accounts-grid{grid-template-columns:1fr;}
  .vt-title{font-size:15px;}
}
`;
